import { useState, useCallback, useEffect } from 'react';

/**
 * Hook para gerenciar a navegação da timeline
 * 
 * @param {Object} options - Opções de configuração
 * @param {Date} options.date - Data atual do calendário
 * @param {Function} options.setDate - Função para atualizar a data
 * @param {string} options.currentView - Visualização atual
 * @param {Function} options.setCurrentView - Função para atualizar a visualização
 * @param {Object} options.customViews - Objeto com as visualizações disponíveis
 * @param {Function} options.onDateChange - Função para notificar mudanças de data
 * @param {Function} options.onViewChange - Função para notificar mudanças de visualização
 * @returns {Object} - Objeto com estados e funções para navegação da timeline
 */
const useTimelineNavigation = ({ 
  date, 
  setDate, 
  currentView,
  setCurrentView,
  customViews = {}, 
  onDateChange,
  onViewChange 
}) => {
  // Função para navegar entre datas (compatível com React Big Calendar)
  const handleDateNavigate = useCallback((action, newDate) => {
    // Se receber uma data diretamente, usá-la
    if (action instanceof Date) {
      setDate(action);
      if (typeof onDateChange === 'function') {
        const formattedDate = formatDateToString(action);
        onDateChange(formattedDate);
      }
      return;
    }
    
    // Se receber uma ação de navegação e uma data opcional
    let dateToNavigate = newDate;
    
    // Se não receber uma data, calcular com base na ação
    if (!dateToNavigate) {
      switch (action) {
        case 'TODAY':
          dateToNavigate = new Date();
          break;
        case 'PREV':
          dateToNavigate = calculatePrevDate(date, currentView);
          break;
        case 'NEXT':
          dateToNavigate = calculateNextDate(date, currentView);
          break;
        case 'DATE':
          // Neste caso, esperamos que newDate seja fornecido
          if (!newDate) return;
          dateToNavigate = newDate;
          break;
        default:
          // Se não reconhecer a ação, não fazer nada
          return;
      }
    }
    
    setDate(dateToNavigate);
    
    // Chamar callback externo se fornecido
    if (typeof onDateChange === 'function') {
      // Formatar a data para YYYY-MM-DD para manter compatibilidade
      const formattedDate = formatDateToString(dateToNavigate);
      onDateChange(formattedDate);
    }
  }, [date, currentView, setDate, onDateChange]);
  
  // Formatar data para string YYYY-MM-DD
  const formatDateToString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  // Calcular data anterior com base na visualização
  const calculatePrevDate = (currentDate, view) => {
    const newDate = new Date(currentDate);
    
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week' || view?.includes('resource')) {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    
    return newDate;
  };
  
  // Calcular próxima data com base na visualização
  const calculateNextDate = (currentDate, view) => {
    const newDate = new Date(currentDate);
    
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week' || view?.includes('resource')) {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    
    return newDate;
  };
  
  // Função para navegar entre visualizações
  const handleViewNavigate = useCallback((newView) => {
    setCurrentView(newView);
    
    // Chamar callback externo se fornecido
    if (typeof onViewChange === 'function') {
      onViewChange(newView);
    }
  }, [setCurrentView, onViewChange]);
  
  // Função para navegar para datas específicas
  const goToToday = useCallback(() => {
    handleDateNavigate('TODAY');
  }, [handleDateNavigate]);
  
  const goToNextDay = useCallback(() => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    handleDateNavigate(nextDay);
  }, [date, handleDateNavigate]);
  
  const goToPreviousDay = useCallback(() => {
    const prevDay = new Date(date);
    prevDay.setDate(prevDay.getDate() - 1);
    handleDateNavigate(prevDay);
  }, [date, handleDateNavigate]);
  
  const goToNextWeek = useCallback(() => {
    const nextWeek = new Date(date);
    nextWeek.setDate(nextWeek.getDate() + 7);
    handleDateNavigate(nextWeek);
  }, [date, handleDateNavigate]);
  
  const goToPreviousWeek = useCallback(() => {
    const prevWeek = new Date(date);
    prevWeek.setDate(prevWeek.getDate() - 7);
    handleDateNavigate(prevWeek);
  }, [date, handleDateNavigate]);
  
  const goToNextMonth = useCallback(() => {
    const nextMonth = new Date(date);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    handleDateNavigate(nextMonth);
  }, [date, handleDateNavigate]);
  
  const goToPreviousMonth = useCallback(() => {
    const prevMonth = new Date(date);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    handleDateNavigate(prevMonth);
  }, [date, handleDateNavigate]);
  
  // Função para ajustar o número de colunas em visualizações de recursos
  const handleColumnAdjustment = useCallback((columnCount) => {
    console.log(`Ajustando para ${columnCount} colunas`);
    // Implementar lógica de ajuste de colunas se necessário
  }, []);
  
  // Função para navegação específica da visualização
  const navigateByView = useCallback((action) => {
    switch (action) {
      case 'PREV':
        handleDateNavigate('PREV');
        break;
      case 'NEXT':
        handleDateNavigate('NEXT');
        break;
      case 'TODAY':
        handleDateNavigate('TODAY');
        break;
      default:
        console.warn(`Ação de navegação desconhecida: ${action}`);
    }
  }, [handleDateNavigate]);
  
  // Usar ResizeObserver para ajustar automaticamente o número de colunas em telas menores
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // Ajustar o número de colunas com base na largura da tela
      if (width < 640) {
        // Telas muito pequenas - mostrar apenas 1 coluna
        handleColumnAdjustment(1);
      } else if (width < 768) {
        // Telas pequenas - mostrar 2 colunas
        handleColumnAdjustment(2);
      } else if (width < 1024) {
        // Telas médias - mostrar 3 colunas
        handleColumnAdjustment(3);
      } else if (width < 1280) {
        // Telas grandes - mostrar 4 colunas
        handleColumnAdjustment(4);
      } else {
        // Telas muito grandes - mostrar 5 colunas
        handleColumnAdjustment(5);
      }
    };
    
    // Inicializar com o tamanho atual
    handleResize();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', handleResize);
    
    // Limpar listener ao desmontar
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleColumnAdjustment]);
  
  return {
    date,
    currentView,
    handleDateNavigate,
    handleViewNavigate,
    navigateByView,
    goToToday,
    goToNextDay,
    goToPreviousDay,
    goToNextWeek,
    goToPreviousWeek,
    goToNextMonth,
    goToPreviousMonth,
    handleColumnAdjustment
  };
};

export default useTimelineNavigation; 